import { useEffect, useMemo, useState } from "react";

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  Autocomplete,
} from "@mui/material";

import { Movement, MovementArticle } from "types";
import { MovementTypes } from "../../constants/index";
import { DatePicker } from "@mui/x-date-pickers";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import { useSession } from "providers/SessionProvider";
import { calculateAvailableArticleLocationStock } from "helpers/movement";

type Props = {
  movement: Movement;
  open: boolean;
  handleOpen: () => void;
  value: MovementArticle;
  onEdit: (movement: MovementArticle) => void;
  locations?: { id: number; code: string; store_id?: number }[];
};

const normalizeMovementArticleForEdit = (
  article: MovementArticle
): MovementArticle => {
  if (article.is_reserved && article.reserved_reference_amount == null) {
    return {
      ...article,
      reserved_reference_amount: Number(article.amount) || 0,
    };
  }

  return article;
};

export default function EditMovementModal({
  movement,
  open = true,
  handleOpen,
  value,
  onEdit,
  locations
}: Props) {
  const [data, setData] = useState<MovementArticle>(() =>
    normalizeMovementArticleForEdit(value)
  );
  const { clients } = useSession();
  const client = useMemo(
    () => clients.find(c => c.id === data.client_id),
    [clients, data.client_id]
  );

  const amountNum = Number(data.amount) || 0;
  const currentArticleLocationId =
    data.article_locations?.id ?? data.article_locations_id;

  const available = useMemo(() => {
    return calculateAvailableArticleLocationStock({
      movementArticles: movement.articles,
      articleLocationId: currentArticleLocationId,
      stock: data.article_locations?.stock,
      reserved: data.article_locations?.reserved,
      notAvailable: data.article_locations?.not_available,
      inWorkOrders: data.article_locations?.in_work_orders,
      excludeMovementArticleId: data.id,
    });
  }, [
    movement.articles,
    currentArticleLocationId,
    data.article_locations?.stock,
    data.article_locations?.reserved,
    data.article_locations?.not_available,
    data.article_locations?.in_work_orders,
    data.id,
  ]);
  const isIncome    = movement?.movement_type_id === MovementTypes.Income;
  const isDischarge = movement?.movement_type_id === MovementTypes.Discharge;
  const isPedido    = movement?.movement_type_id === MovementTypes.Preparation;

  const al = data.article_locations;

  const locationOptions = useMemo(() => {
    const currId = al.location?.id ?? al.location_id;
    const currLabel = al.location?.code ?? String(currId ?? "");

    const base = movement.store_id
      ? (locations ?? []).filter(l => l.store_id === movement.store_id)
      : (locations ?? []);

    const mapped = base.map(l => ({ id: l.id, label: l.code }));

    if (currId != null && !mapped.some(o => o.id === currId)) {
      return [{ id: currId as number, label: currLabel }, ...mapped];
    }
    return mapped;
  }, [locations, movement.store_id, al.location_id, al.location?.id, al.location?.code]);


  const setALField = <K extends keyof typeof al>(key: K, val: typeof al[K]) => {
    setData(prev => ({
      ...prev,
      article_locations: {
        ...prev.article_locations,
        [key]: val,
      },
    }));
  };

  const exceedsAvailable = (isDischarge || isPedido) && amountNum > available;

  const isSaveDisabled =
    amountNum <= 0 || ((isDischarge || isPedido) && amountNum > available);

  const handleChange = (name: string, value: string) => {
    if (name === "amount") {
      const nextAmount = Number(value);

      setData({
        ...data,
        [name]: nextAmount,
        subtotal: nextAmount,
        article_locations: {
          ...data.article_locations,
          not_available:
            isIncome && Number(data.article_locations?.not_available ?? 0) > 0
              ? nextAmount
              : data.article_locations?.not_available,
        },
      });
      return;
    }

    setData({
      ...data,
      [name]: value,
    });
  };

  const onSubmit = (updatedMovement: MovementArticle) => {
    onEdit(updatedMovement);
  };

  useEffect(() => {
    setData(normalizeMovementArticleForEdit(value));
  }, [value]);

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "30rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h5" gutterBottom>
            Editar Transacción
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="outlined-required"
              value={data.article_id}
              fullWidth
              defaultValue={data.article_id}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="outlined-required"
              label="Descripción"
              fullWidth
              value={data.description}
              defaultValue={data.description}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="amount"
              label="Cantidad"
              type="number"
              fullWidth
              value={data.amount ?? ""}
              onChange={(e) => handleChange("amount", e.target.value)}
              error={exceedsAvailable}
              helperText={
              (isDischarge || isPedido)
                ? exceedsAvailable
                  ? `La cantidad a egresar es mayor al stock disponible (${available})`
                  : `Stock disponible: ${available}`
                : ""
            }
            />
          </Grid>
          {client?.show_lote && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Lote"
                fullWidth
                value={al.lote ?? ""}
                onChange={(e) => setALField("lote", e.target.value || null)}
                disabled={!isIncome}
              />
            </Grid>
          )}
          {client?.show_oc && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="OC"
                fullWidth
                value={al.oc ?? ""}
                onChange={(e) => setALField("oc", e.target.value || null)}
                disabled={!isIncome}
              />
            </Grid>
          )}
          {client?.show_fecha_vencimiento && (
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Fecha de venc."
                value={al.fecha_vencimiento ? new Date(al.fecha_vencimiento as any) : null}
                onChange={(v) => {
                  try {
                    setALField("fecha_vencimiento", v ? v.toISOString() : null);
                  } catch {}
                }}
                slots={{ actionBar: DateTimePickerActionBar, textField: TextField }}
                slotProps={{ textField: { fullWidth: true } }}
                disabled={!isIncome}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={locationOptions}
              value={{
                id: al.location?.id ?? al.location_id,
                label: al.location?.code ?? String(al.location_id ?? ""),
              }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_e, v) => {
                if (!v) return;
                setALField("location_id", v.id as number);
                setALField("location", { ...(al.location ?? {}), id: v.id as number, code: v.label } as any);
              }}
              renderInput={(params) => <TextField {...params} label="Ubicación" fullWidth />}
              disabled={!isIncome}
            />
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                onSubmit(data);
                handleOpen();
              }}
              disabled={isSaveDisabled}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
